import React from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Check } from "lucide-react";
import { brand, chartColors, neutral } from "../../theme/palette";
import { ChartCard, Legend, SegmentedControl, axisProps } from "../Analytics/AnalyticsUI";
import { formatINR } from "../../utils/formatINR";
import { formatCompact, formatCompactINR, formatDecimal, formatNumber } from "../../utils/analyticsMetrics";
import { METRICS, formatHours } from "../../utils/stationHistory";

export const MAX_COMPARE = chartColors.categorical.length;

// value formatting per metric: full (tooltips, tables) and compact (axis ticks)
export const FORMAT = {
  final: { full: (v) => formatINR(v), axis: (v) => formatCompactINR(v) },
  base: { full: (v) => formatINR(v), axis: (v) => formatCompactINR(v) },
  gst: { full: (v) => formatINR(v), axis: (v) => formatCompactINR(v) },
  energy: { full: (v) => `${formatDecimal(v)} kWh`, axis: (v) => formatCompact(v) },
  sessions: { full: (v) => formatNumber(v), axis: (v) => formatCompact(v) },
  duration: { full: (v) => formatHours(v), axis: (v) => `${formatCompact(v / 3600)}h` },
};

const grid = <CartesianGrid vertical={false} stroke={chartColors.grid} strokeDasharray="3 3" />;
const xAxis = <XAxis dataKey="label" {...axisProps} minTickGap={18} interval="preserveStartEnd" tickMargin={8} />;
const yAxis = (fmt) => <YAxis {...axisProps} width={56} tickFormatter={fmt} />;
const cursor = { stroke: neutral[300], strokeWidth: 1 };
const barCursor = { fill: neutral[100] };

// tooltip: the bucket label, then rows of [color, name, value]
const Tip = ({ active, payload, label, rows }) => {
  if (!active || !payload?.length) return null;
  const items = rows(payload[0].payload, payload);
  return (
    <div className="min-w-[180px] rounded-lg border border-line bg-white px-3 py-2.5 shadow-lg">
      <p className="mb-1.5 text-xs font-medium text-muted">{label}</p>
      <div className="space-y-1">
        {items.map((r) => (
          <div key={r.name} className="flex items-center justify-between gap-4 text-13">
            <span className="flex items-center gap-2 text-neutral-600">
              {r.color ? <span className="h-2 w-2 rounded-full" style={{ background: r.color }} /> : <span className="w-2" />}
              {r.name}
            </span>
            <span className={`tabular-nums text-fg ${r.strong ? "font-semibold" : "font-medium"}`}>{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// plain table version of a time series (the "Table" view)
export const SeriesTable = ({ rows, columns }) => (
  <div className="max-h-[340px] overflow-auto rounded-lg border border-line">
    <table className="min-w-full text-13">
      <thead className="sticky top-0 bg-neutral-50">
        <tr>
          <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Period</th>
          {columns.map((c) => (
            <th key={c.key} className="whitespace-nowrap px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-neutral-100">
        {[...rows].reverse().map((r) => (
          <tr key={r.bucket}>
            <td className="whitespace-nowrap px-4 py-2 text-neutral-700">{r.label}</td>
            {columns.map((c) => (
              <td key={c.key} className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-neutral-700">
                {c.format(r[c.key])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ---------- 3. revenue & energy trends ----------

export const RevenueEnergyTrends = ({ series, view, isLoading, granularityLabel }) => {
  const isEmpty = !series.some((r) => r.final || r.energy || r.sessions);
  if (view === "table") {
    return (
      <ChartCard title="Revenue and energy" description={`${granularityLabel} totals`} isLoading={isLoading} isEmpty={isEmpty} height={200}>
        <SeriesTable
          rows={series}
          columns={[
            { key: "base", label: "Base cost", format: FORMAT.base.full },
            { key: "gst", label: "GST", format: FORMAT.gst.full },
            { key: "final", label: "Final revenue", format: FORMAT.final.full },
            { key: "energy", label: "Energy", format: FORMAT.energy.full },
          ]}
        />
      </ChartCard>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <ChartCard title="Final revenue" description={`${granularityLabel} revenue including GST`} isLoading={isLoading} isEmpty={isEmpty}>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="hist-revenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={brand[600]} stopOpacity={0.18} />
                <stop offset="100%" stopColor={brand[600]} stopOpacity={0} />
              </linearGradient>
            </defs>
            {grid}
            {xAxis}
            {yAxis(FORMAT.final.axis)}
            <Tooltip
              cursor={cursor}
              content={(p) => (
                <Tip
                  {...p}
                  rows={(d) => [
                    { name: "Base cost", value: formatINR(d.base) },
                    { name: "GST", value: formatINR(d.gst) },
                    { name: "Final revenue", value: formatINR(d.final), color: brand[600], strong: true },
                  ]}
                />
              )}
            />
            <Area type="monotone" dataKey="final" name="Final revenue" stroke={brand[600]} strokeWidth={2} fill="url(#hist-revenue)" activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Energy delivered" description={`${granularityLabel} energy in kWh`} isLoading={isLoading} isEmpty={isEmpty}>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            {grid}
            {xAxis}
            {yAxis(FORMAT.energy.axis)}
            <Tooltip
              cursor={barCursor}
              content={(p) => (
                <Tip
                  {...p}
                  rows={(d) => [
                    { name: "Energy", value: FORMAT.energy.full(d.energy), color: chartColors.secondary, strong: true },
                    { name: "Sessions", value: formatNumber(d.sessions) },
                  ]}
                />
              )}
            />
            <Bar dataKey="energy" name="Energy" fill={chartColors.secondary} radius={[4, 4, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
};

// ---------- 4. station-wise comparison ----------

const METRIC_OPTIONS = Object.entries(METRICS).map(([value, m]) => ({ value, label: m.label }));

export const StationComparison = ({
  series,
  totals,
  compare,
  colorOf,
  onToggle,
  nameOf,
  metric,
  onMetricChange,
  view,
  isLoading,
  granularityLabel,
}) => {
  const key = METRICS[metric].key;
  const fmt = FORMAT[key];
  const ranked = [...totals].sort((a, b) => b[key] - a[key]);
  const max = Math.max(...ranked.map((t) => t[key]), 0);
  const full = compare.length >= MAX_COMPARE;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <ChartCard
        className="xl:col-span-2"
        title={`${METRICS[metric].label} by station`}
        description={`${granularityLabel} values for the stations selected on the right`}
        actions={<SegmentedControl options={METRIC_OPTIONS} value={metric} onChange={onMetricChange} />}
        isLoading={isLoading}
        isEmpty={!compare.length || !series.length}
        emptyText={totals.length ? "Pick stations on the right to compare them" : "No data for the selected period"}
        height={300}
      >
        {view === "table" ? (
          <SeriesTable rows={series} columns={compare.map((id) => ({ key: id, label: nameOf(id), format: fmt.full }))} />
        ) : (
          <div className="space-y-3">
            <Legend items={compare.map((id) => ({ label: nameOf(id), color: colorOf(id), line: true }))} />
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                {grid}
                {xAxis}
                {yAxis(fmt.axis)}
                <Tooltip
                  cursor={cursor}
                  content={(p) => (
                    <Tip
                      {...p}
                      rows={(d) =>
                        [...compare]
                          .sort((a, b) => d[b] - d[a])
                          .map((id) => ({ name: nameOf(id), value: fmt.full(d[id]), color: colorOf(id) }))
                      }
                    />
                  )}
                />
                {compare.map((id) => (
                  <Line
                    key={id}
                    type="monotone"
                    dataKey={id}
                    name={nameOf(id)}
                    stroke={colorOf(id)}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>

      <div className="card flex flex-col p-5">
        <div className="mb-3">
          <h3 className="section-title">Station ranking</h3>
          <p className="mt-0.5 text-13 text-muted">
            Total {METRICS[metric].label.toLowerCase()} for the period. Click to compare (up to {MAX_COMPARE}).
          </p>
        </div>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-8 animate-pulse rounded bg-neutral-100" />
            ))}
          </div>
        ) : ranked.length === 0 ? (
          <p className="py-10 text-center text-13 text-muted">No stations in this period.</p>
        ) : (
          <ul className="-mx-2 max-h-[340px] space-y-0.5 overflow-y-auto pr-1">
            {ranked.map((t, i) => {
              const selected = compare.includes(t.stationId);
              const disabled = !selected && full;
              return (
                <li key={t.stationId}>
                  <button
                    type="button"
                    onClick={() => onToggle(t.stationId)}
                    disabled={disabled}
                    title={disabled ? `Up to ${MAX_COMPARE} stations can be compared` : selected ? "Remove from comparison" : "Add to comparison"}
                    className={`group w-full rounded-md px-2 py-1.5 text-left transition-colors ${
                      selected ? "bg-neutral-50" : "hover:bg-neutral-50"
                    } disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded ${selected ? "" : "ring-1 ring-inset ring-neutral-300"}`}
                        style={selected ? { background: colorOf(t.stationId) } : undefined}
                      >
                        {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                      </span>
                      <span className="w-5 flex-shrink-0 text-[11px] tabular-nums text-neutral-400">{i + 1}</span>
                      <span className="min-w-0 flex-1 truncate text-13 font-medium text-neutral-800">{nameOf(t.stationId)}</span>
                      <span className="flex-shrink-0 text-13 font-semibold tabular-nums text-fg">{fmt.full(t[key])}</span>
                    </div>
                    <div className="ml-[52px] mt-1 h-1.5 overflow-hidden rounded-full bg-neutral-100">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${max ? (t[key] / max) * 100 : 0}%`, background: selected ? colorOf(t.stationId) : neutral[400] }}
                      />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------- 5. charging session analytics ----------

const Stat = ({ label, value, hint }) => (
  <div className="rounded-lg border border-line bg-neutral-50/60 px-4 py-3">
    <p className="text-xs font-medium text-neutral-500">{label}</p>
    <p className="mt-1 text-lg font-semibold tracking-tight tabular-nums text-fg">{value}</p>
    {hint && <p className="text-[11px] text-neutral-400">{hint}</p>}
  </div>
);

export const SessionAnalytics = ({ series, totals, view, isLoading, granularityLabel }) => {
  const isEmpty = !series.some((r) => r.sessions || r.duration);
  const per = (v) => (totals.sessions ? v / totals.sessions : null);
  const stats = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Stat label="Energy per session" value={per(totals.energy) == null ? "—" : FORMAT.energy.full(per(totals.energy))} hint="Average over the period" />
      <Stat label="Time per session" value={per(totals.duration) == null ? "—" : formatHours(per(totals.duration))} hint="Average over the period" />
      <Stat label="Revenue per session" value={per(totals.final) == null ? "—" : formatINR(per(totals.final))} hint="Final revenue, average" />
    </div>
  );

  if (view === "table") {
    return (
      <div className="space-y-4">
        {!isLoading && stats}
        <ChartCard title="Sessions and charging time" description={`${granularityLabel} totals`} isLoading={isLoading} isEmpty={isEmpty} height={200}>
          <SeriesTable
            rows={series}
            columns={[
              { key: "sessions", label: "Sessions", format: FORMAT.sessions.full },
              { key: "duration", label: "Charging time", format: FORMAT.duration.full },
            ]}
          />
        </ChartCard>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!isLoading && stats}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ChartCard title="Charging sessions" description={`${granularityLabel} session count`} isLoading={isLoading} isEmpty={isEmpty}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              {grid}
              {xAxis}
              {yAxis(FORMAT.sessions.axis)}
              <Tooltip
                cursor={barCursor}
                content={(p) => <Tip {...p} rows={(d) => [{ name: "Sessions", value: formatNumber(d.sessions), color: brand[600], strong: true }]} />}
              />
              <Bar dataKey="sessions" name="Sessions" fill={brand[600]} radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Charging time" description={`${granularityLabel} hours of charging`} isLoading={isLoading} isEmpty={isEmpty}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="hist-duration" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColors.secondary} stopOpacity={0.14} />
                  <stop offset="100%" stopColor={chartColors.secondary} stopOpacity={0} />
                </linearGradient>
              </defs>
              {grid}
              {xAxis}
              {yAxis(FORMAT.duration.axis)}
              <Tooltip
                cursor={cursor}
                content={(p) => (
                  <Tip
                    {...p}
                    rows={(d) => [
                      { name: "Charging time", value: formatHours(d.duration), color: chartColors.secondary, strong: true },
                      { name: "Sessions", value: formatNumber(d.sessions) },
                    ]}
                  />
                )}
              />
              <Area type="monotone" dataKey="duration" name="Charging time" stroke={chartColors.secondary} strokeWidth={2} fill="url(#hist-duration)" activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};
